import { z } from 'zod';
import { terraContract } from '@terra/shared-authorization';
import {
  ExchangeCodeRequestSchema,
  ExchangeCodeResponseSchema,
  GetAuthConfigResponseSchema,
  LocalLoginRequestSchema,
  LocalLoginTokenResponseSchema,
  OidcSessionResponseSchema,
  RefreshTokenRequestSchema,
  UnauthorizedErrorSchema,
} from '@terra/shared-contracts-gateway-auth';

export const COCKPIT_AUTH_PATH_NAME = 'auth';

export const cockpitAuthRoutes = {
  getAuthConfig: terraContract
    .meta({ public: true })
    .route({
      method: 'GET',
      path: '/config',
      summary: 'Get Cockpit public auth configuration',
      inputStructure: 'detailed',
    })
    .output(GetAuthConfigResponseSchema),
  exchangeCode: terraContract
    .meta({ public: true })
    .route({
      method: 'POST',
      path: '/oauth/token',
      summary: 'Exchange OAuth code for Cockpit session tokens',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: ExchangeCodeRequestSchema }))
    .output(z.union([OidcSessionResponseSchema, ExchangeCodeResponseSchema]))
    .errors({ UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema } }),
  refreshToken: terraContract
    .meta({ public: true })
    .route({
      method: 'POST',
      path: '/oauth/refresh',
      summary: 'Refresh Cockpit OAuth access token',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: RefreshTokenRequestSchema }))
    .output(z.union([OidcSessionResponseSchema, ExchangeCodeResponseSchema]))
    .errors({ UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema } }),
  logout: terraContract
    .meta({ public: true })
    .route({
      method: 'POST',
      path: '/oauth/logout',
      summary: 'Clear Cockpit OIDC session',
      inputStructure: 'detailed',
      successStatus: 204,
    })
    .output(z.null())
    .errors({ UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema } }),
  getSession: terraContract
    .meta({ public: true })
    .route({
      method: 'GET',
      path: '/oauth/session',
      summary: 'Get current Cockpit OIDC session',
      inputStructure: 'detailed',
    })
    .output(OidcSessionResponseSchema)
    .errors({ UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema } }),
  localLogin: terraContract
    .meta({ public: true })
    .route({
      method: 'POST',
      path: '/login',
      summary: 'Cockpit local username/password login',
      inputStructure: 'detailed',
    })
    .input(z.object({ body: LocalLoginRequestSchema }))
    .output(z.union([OidcSessionResponseSchema, LocalLoginTokenResponseSchema]))
    .errors({ UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema } }),
  localGetSession: terraContract
    .meta({ public: true })
    .route({
      method: 'GET',
      path: '/local/session',
      summary: 'Get current Cockpit local auth session',
      inputStructure: 'detailed',
    })
    .output(OidcSessionResponseSchema)
    .errors({ UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema } }),
  localLogout: terraContract
    .meta({ public: true })
    .route({
      method: 'POST',
      path: '/local/logout',
      summary: 'Clear Cockpit local auth session',
      inputStructure: 'detailed',
      successStatus: 204,
    })
    .output(z.null())
    .errors({ UNAUTHORIZED: { status: 401, data: UnauthorizedErrorSchema } }),
} as const;

export const cockpitAuthContract = terraContract
  .prefix(`/${COCKPIT_AUTH_PATH_NAME}`)
  .router(cockpitAuthRoutes);
